import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AccessControlGroupResponse,
  AccessControlGroupType,
} from "api/ApiClient";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import { X } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Combobox } from "components/ui/combobox";

const LOCK_GROUPS_QUERY_KEY = (lockId: string) => ["locks", "groups", lockId];
const AVAILABLE_LOCK_GROUPS_QUERY_KEY = (lockId: string, search: string) => [
  "locks",
  "groups",
  "available",
  lockId,
  search,
];
const ASSIGN_SEARCH_DEBOUNCE_MS = 400;

function extractErrorMessage(error: unknown, defaultMessage: string): string {
  const findMessageInPayload = (payload: any): string | null => {
    if (!payload) return null;

    if (typeof payload === "string") {
      try {
        const parsed = JSON.parse(payload);
        return findMessageInPayload(parsed);
      } catch {
        return payload.trim() ? payload : null;
      }
    }

    if (typeof payload !== "object") return null;

    const queue: any[] = [payload];
    const seen = new Set<any>();

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || typeof current !== "object" || seen.has(current)) continue;
      seen.add(current);

      const directMessage =
        typeof current.message === "string" && current.message.trim()
          ? current.message
          : typeof current.title === "string" && current.title.trim()
            ? current.title
            : null;

      if (directMessage) {
        return directMessage;
      }

      if (Array.isArray(current)) {
        queue.push(...current);
        continue;
      }

      if (current.errors && typeof current.errors === "object") {
        queue.push(current.errors);
      }

      for (const value of Object.values(current)) {
        if (value && (typeof value === "object" || typeof value === "string")) {
          queue.push(value);
        }
      }
    }

    return null;
  };

  const err = error as any;
  const candidates = [
    err?.response?.data,
    err?.data,
    err?.response,
    err,
  ];

  for (const candidate of candidates) {
    const message = findMessageInPayload(candidate);
    if (message) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) return error.message;
  return defaultMessage;
}

function formatGroupType(type: string | undefined): string {
  if (!type) return "—";
  return type
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function matchesGroupSearch(
  group: AccessControlGroupResponse,
  search: string,
): boolean {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  const haystack = [
    group.name,
    group.id,
    group.propertyExternalId,
    formatGroupType(group.type),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (haystack.includes(normalizedSearch)) {
    return true;
  }

  let searchIndex = 0;
  for (
    let haystackIndex = 0;
    haystackIndex < haystack.length && searchIndex < normalizedSearch.length;
    haystackIndex += 1
  ) {
    if (haystack[haystackIndex] === normalizedSearch[searchIndex]) {
      searchIndex += 1;
    }
  }

  return searchIndex === normalizedSearch.length;
}

function useGetLockGroups(lockId: string) {
  return useQuery({
    queryKey: LOCK_GROUPS_QUERY_KEY(lockId),
    queryFn: async () => {
      const response = await axios.get<AccessControlGroupResponse[]>(
        `${BASE_API_URL}/api/Locks/${lockId}/accessControlGroups`,
      );
      return response.data;
    },
  });
}

function useGetAvailableLockGroups(lockId: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: AVAILABLE_LOCK_GROUPS_QUERY_KEY(lockId, search),
    queryFn: async () => {
      const response = await axios.get<AccessControlGroupResponse[]>(
        `${BASE_API_URL}/api/Locks/${lockId}/accessControlGroups/available`,
        {
          params: {
            page: 0,
            pageSize: 500,
          },
        },
      );
      return response.data;
    },
    enabled,
  });
}

export default function LockGroups({ lockId }: { lockId: string }) {
  const queryClient = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearchInput, setAssignSearchInput] = useState<string>("");
  const [assignSearch, setAssignSearch] = useState<string>("");
  const [selectedGroup, setSelectedGroup] =
    useState<AccessControlGroupResponse | null>(null);
  const [selectedTypeFilters, setSelectedTypeFilters] =
    useState<AccessControlGroupType[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: groups,
    isError,
    isLoading,
  } = useGetLockGroups(lockId);

  const {
    data: availableGroups,
    isLoading: isLoadingAvailable,
  } = useGetAvailableLockGroups(lockId, assignSearch, assignOpen);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAssignSearch(assignSearchInput);
    }, ASSIGN_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [assignSearchInput]);

  const sortedGroups = useMemo(() => {
    if (!groups) return [];
    return [...groups].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [groups]);

  const sortedAvailableGroups = useMemo(() => {
    if (!availableGroups) return [];
    return [...availableGroups].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? ""),
    );
  }, [availableGroups]);

  const availableGroupTypes = useMemo(() => {
    const unique = new Set(
      sortedAvailableGroups
        .map((group) => group.type)
        .filter((type): type is AccessControlGroupType => !!type),
    );
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [sortedAvailableGroups]);

  const filteredAvailableGroups = useMemo(() => {
    const typeFilteredGroups =
      selectedTypeFilters.length === 0
        ? sortedAvailableGroups
        : sortedAvailableGroups.filter(
            (group) => !!group.type && selectedTypeFilters.includes(group.type),
          );

    return typeFilteredGroups.filter((group) =>
      matchesGroupSearch(group, assignSearch),
    );
  }, [assignSearch, selectedTypeFilters, sortedAvailableGroups]);

  const toggleTypeFilter = (type: AccessControlGroupType) => {
    setSelectedTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((value) => value !== type) : [...prev, type],
    );
  };

  const removeMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await axios.delete(
        `${BASE_API_URL}/api/Locks/${lockId}/accessControlGroups/${groupId}`,
      );
    },
    onSuccess: async () => {
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: LOCK_GROUPS_QUERY_KEY(lockId) });
      await queryClient.invalidateQueries({
        queryKey: ["locks", "groups", "available", lockId],
      });
    },
    onError: (error) => {
      setActionError(
        extractErrorMessage(error, "An error occurred while removing the group."),
      );
    },
  });

  const assignMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const response = await axios.post(
        `${BASE_API_URL}/api/Locks/${lockId}/accessControlGroups/${groupId}`,
        undefined,
        {
          // Handle response status ourselves so we can always extract API validation payloads.
          validateStatus: () => true,
        },
      );

      if (response.status >= 400) {
        const resolvedMessage = extractErrorMessage(
          { data: response.data },
          "An error occurred while assigning the group.",
        );
        throw new Error(resolvedMessage);
      }
    },
    onSuccess: async () => {
      setActionError(null);
      setSelectedGroup(null);
      setAssignSearchInput("");
      setAssignSearch("");
      setSelectedTypeFilters([]);
      setAssignOpen(false);
      await queryClient.invalidateQueries({ queryKey: LOCK_GROUPS_QUERY_KEY(lockId) });
      await queryClient.invalidateQueries({
        queryKey: ["locks", "groups", "available", lockId],
      });
    },
    onError: (error) => {
      const resolvedMessage = extractErrorMessage(
        error,
        "An error occurred while assigning the group.",
      );
      setActionError(resolvedMessage);
    },
  });

  const onRemoveGroup = (group: AccessControlGroupResponse) => {
    if (!group.id) return;

    const confirmed = window.confirm(
      `Remove lock from group "${group.name ?? group.id}"?`,
    );

    if (!confirmed) {
      return;
    }

    removeMutation.mutate(group.id);
  };

  const onAssignGroup = () => {
    if (!selectedGroup?.id) return;
    assignMutation.mutate(selectedGroup.id);
  };

  const isBusy = removeMutation.isPending || assignMutation.isPending;

  const closeAssignDialog = () => {
    if (isBusy) return;

    setSelectedGroup(null);
    setAssignSearchInput("");
    setAssignSearch("");
    setSelectedTypeFilters([]);
    setActionError(null);
    setAssignOpen(false);
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <p style={{ margin: 0, color: "#666", fontSize: 13 }}>
          Manage access control group memberships for this lock. Assign or
          remove groups to control where this lock is included.
        </p>
        {sortedGroups.length > 0 ? (
          <Button
            onClick={() => {
              setActionError(null);
              setAssignOpen(true);
            }}
            type="button"
          >
            Assign Group
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div>Loading groups...</div>
      ) : isError ? (
        <div>Error loading groups.</div>
      ) : sortedGroups.length === 0 ? (
        <div
          style={{
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              No groups yet
            </div>
            <div style={{ color: "#6b7280", fontSize: 13 }}>
              Assign this lock to an access control group to manage shared access rules.
            </div>
          </div>
          <Button
            onClick={() => {
              setActionError(null);
              setAssignOpen(true);
            }}
            type="button"
          >
            Assign Group
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col">Group</th>
                  <th scope="col">Type</th>
                  <th scope="col">Property</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
            {sortedGroups.map((group) => (
              <tr key={group.id}>
                <td>
                  {group.id ? (
                    <Link to={`/admin/accessControlGroups/${group.id}`}>
                      {group.name ?? group.id}
                    </Link>
                  ) : (
                    group.name ?? "—"
                  )}
                </td>
                <td>
                  <Badge variant="outline">{formatGroupType(group.type)}</Badge>
                </td>
                <td>
                  {group.propertyExternalId ? (
                    <Link to={`/admin/properties/${group.propertyExternalId}`}>
                      {group.propertyExternalId}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      onClick={() => onRemoveGroup(group)}
                      disabled={!group.id || isBusy}
                      size="sm"
                      type="button"
                      variant="destructive"
                    >
                      {removeMutation.isPending ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog
        open={assignOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeAssignDialog();
          }
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          showCloseButton={false}
          className="sm:max-w-lg"
          onEscapeKeyDown={(event) => {
            if (isBusy) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (isBusy) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Assign Group</DialogTitle>
          </DialogHeader>
          <div className="mt-1 grid gap-4">
            {actionError && (
              <Alert variant="destructive">
                {actionError}
              </Alert>
            )}
            {!selectedGroup && availableGroupTypes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Badge asChild variant={selectedTypeFilters.length === 0 ? "default" : "outline"}>
                  <button
                    className="cursor-pointer"
                    onClick={() => setSelectedTypeFilters([])}
                    type="button"
                  >
                    All
                  </button>
                </Badge>
                {availableGroupTypes.map((type) => {
                  const active = selectedTypeFilters.includes(type);
                  return (
                    <Badge
                      asChild
                      key={type}
                      variant={active ? "default" : "outline"}
                    >
                      <button
                        className="cursor-pointer"
                        onClick={() => toggleTypeFilter(type)}
                        type="button"
                      >
                        {formatGroupType(type)}
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            {selectedGroup ? (
              <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 p-3">
                <div className="flex-1">
                  <div style={{ fontWeight: 600, color: "#111827" }}>
                    {selectedGroup.name ?? selectedGroup.id ?? "Unknown"}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {formatGroupType(selectedGroup.type)} • {selectedGroup.propertyExternalId || "—"}
                  </div>
                </div>
                <Button
                  aria-label="Clear selected group"
                  size="icon-xs"
                  onClick={() => {
                    setSelectedGroup(null);
                    setAssignSearchInput("");
                    setAssignSearch("");
                    setActionError(null);
                  }}
                  type="button"
                  variant="ghost"
                >
                  <X />
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-slate-700"
                    htmlFor="assign-group"
                  >
                    Group
                  </label>
                  <Combobox
                    ariaLabel="Group"
                    className="w-full"
                    emptyMessage="No groups match your search"
                    filterOptions={(groups, search) =>
                      groups.filter((group) => matchesGroupSearch(group, search))
                    }
                    getOptionKey={(group) =>
                      group.id ?? group.externalId ?? group.name ?? "unknown-group"
                    }
                    getOptionLabel={(group) =>
                      `${group.name ?? group.id ?? "Unknown"} (${formatGroupType(group.type)})`
                    }
                    id="assign-group"
                    inputValue={assignSearchInput}
                    isOptionEqualToValue={(group, value) => group.id === value.id}
                    isLoading={isLoadingAvailable}
                    loadingMessage="Loading groups..."
                    onClear={() => {
                      setSelectedGroup(null);
                      setAssignSearchInput("");
                      setAssignSearch("");
                    }}
                    onInputValueChange={setAssignSearchInput}
                    onSelectOption={(group) => {
                      setSelectedGroup(group);
                      setAssignSearchInput(
                        `${group.name ?? group.id ?? "Unknown"} (${formatGroupType(group.type)})`,
                      );
                      setAssignSearch("");
                    }}
                    options={filteredAvailableGroups}
                    placeholder="Type to fuzzy-search groups"
                    value={selectedGroup}
                  />
                </div>

                {!isLoadingAvailable && filteredAvailableGroups.length === 0 && (
                  <Alert variant="info">
                    No available groups found for this lock.
                  </Alert>
                )}
              </>
            )}
          </div>
          <DialogFooter>
          <Button
            onClick={() => {
              setActionError(null);
              setAssignOpen(false);
            }}
            disabled={isBusy}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={onAssignGroup}
            disabled={!selectedGroup?.id || isBusy}
            type="button"
          >
            {assignMutation.isPending ? "Assigning..." : "Assign"}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
